// ============================================
// EDIT VIDEO INSIGHTS MODAL
// Manual entry for paid YouTube insights that Creator Partnerships does not
// sync automatically. Separate from the read-only VideoInsightsModal.
// ============================================

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { updatePlannerVideoInsights } from '../PlannerCampaign/api/plannerCampaignApi';
import {
    buildInsightsPayload,
    insightsFormFromVideo,
    NUMERIC_FIELDS,
    RATE_FIELDS,
} from './videoInsightsPayload';

const FIELD_LABELS = {
    paid_view_count: 'Paid views',
    paid_like_count: 'Paid likes',
    paid_comment_count: 'Paid comments',
    paid_share_count: 'Paid shares',
    paid_watch_time: 'Paid watch time (mins)',
    paid_avg_view_duration: 'Paid avg. view duration (secs)',
    engaged_views: 'Engaged views',
    paid_engaged_views: 'Paid engaged views',
    playlist_adds: 'Playlist adds',
    paid_playlist_adds: 'Paid playlist adds',
    paid_engagement_rate: 'Paid engagement rate (%)',
    avg_watch_pct: 'Avg. watch (%)',
};

const fieldStyle = { width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px' };

function EditVideoInsightsModal({ isOpen, video, campaignId, onClose, onSaved }) {
    const [form, setForm] = useState(() => insightsFormFromVideo(video));
    const [saving, setSaving] = useState(false);

    if (!isOpen || !video) return null;

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const handleSubmit = async () => {
        setSaving(true);
        try {
            await updatePlannerVideoInsights(campaignId, buildInsightsPayload(video.youtubeVideoId, form));
            toast.success('Insights updated');
            onSaved && onSaved();
            onClose();
        } catch (error) {
            toast.error(error.message || 'Could not update insights');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }} onClick={onClose}>
            <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid #f3f4f6', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>Edit Video Insights</h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#9ca3af' }}>Manually record paid performance for this video</p>
                </div>
                <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Paid performance</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            {NUMERIC_FIELDS.map((field) => (
                                <div key={field}>
                                    <label style={labelStyle}>{FIELD_LABELS[field] || field}</label>
                                    <input
                                        type="number"
                                        value={form[field] ?? ''}
                                        onChange={(e) => set(field, e.target.value)}
                                        style={fieldStyle}
                                    />
                                </div>
                            ))}
                            {RATE_FIELDS.map((field) => (
                                <div key={field}>
                                    <label style={labelStyle}>{FIELD_LABELS[field] || field}</label>
                                    <input
                                        type="number"
                                        value={form[field] ?? ''}
                                        onChange={(e) => set(field, e.target.value)}
                                        style={fieldStyle}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Engagement &amp; placement</div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginBottom: 12, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={!!form.has_product_placement}
                                onChange={(e) => set('has_product_placement', e.target.checked)}
                                style={{ accentColor: '#dc2626', cursor: 'pointer' }}
                            />
                            Has product placement
                        </label>
                        <div>
                            <label style={labelStyle}>Brand mentions</label>
                            <input
                                type="text"
                                value={form.brand_mentions ?? ''}
                                onChange={(e) => set('brand_mentions', e.target.value)}
                                style={fieldStyle}
                                placeholder="e.g. Brand X, Brand Y"
                            />
                        </div>
                    </div>
                </div>
                <div style={{ padding: '14px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10, position: 'sticky', bottom: 0, background: '#fff' }}>
                    <button onClick={onClose} disabled={saving} style={{ padding: '9px 18px', background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
                    <button onClick={handleSubmit} disabled={saving} style={{ padding: '9px 18px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                        {saving ? 'Saving...' : 'Save Insights'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default EditVideoInsightsModal;
